import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { input, label, btnPrimary, btnGhost, fmtDate, waLink, badge } from "./ui";
import { FileText, Download, MessageCircle, CheckCircle2, AlertTriangle, Send } from "lucide-react";

const WA_MESSAGE = `Olá! Antes do nosso primeiro atendimento, estou encaminhando um documento com as informações e condições do acompanhamento psicológico.
Peço que leia com atenção. Caso tenha alguma dúvida, podemos conversar antes da sessão.
Atenciosamente,
Tacilaine Almeida
Psicóloga — CRP 08/33677`;

const DOC_STATUS = {
  nao_preparado: "Não preparado",
  dados_pendentes: "Dados pendentes",
  pronto_para_revisao: "Pronto para revisão",
  aguardando_envio: "Aguardando envio",
  enviado: "Enviado",
};

export default function PatientDocument({ patient, onClose }) {
  const [data, setData] = useState(null);
  const [cond, setCond] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!patient) return;
    try {
      const { data: d } = await api.get(`/patients/${patient.id}/document-data`);
      setData(d);
      setCond({
        session_price: d.conditions?.session_price ?? "", modality: d.conditions?.modality || "",
        duration_min: d.conditions?.duration_min || 50, frequency: d.conditions?.frequency || "",
        usual_day: d.conditions?.usual_day || "", usual_time: d.conditions?.usual_time || "",
        payment_method: d.conditions?.payment_method || "", payment_due: d.conditions?.payment_due || "",
        cancellation_policy: d.conditions?.cancellation_policy || "",
      });
    } catch { toast.error("Não consegui carregar os dados do documento."); }
  };
  useEffect(() => { load(); }, [patient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!patient) return null;
  const st = data?.doc_status?.status || "nao_preparado";
  const missing = data?.missing_issue || [];
  const lastDoc = data?.documents?.[0];
  const canSend = lastDoc && st !== "enviado";

  const setC = (k, v) => setCond({ ...cond, [k]: v });

  const saveCond = async () => {
    try {
      await api.put(`/patients/${patient.id}/conditions`, {
        ...cond, session_price: cond.session_price === "" ? null : Number(cond.session_price),
        duration_min: Number(cond.duration_min) || 50,
      });
      toast.success("Condições salvas. Valem para novos agendamentos — sessões existentes não mudam.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao salvar condições."); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data: doc } = await api.post(`/patients/${patient.id}/documents`);
      toast.success(`Documento versão ${doc.version} gerado. Revise o PDF antes de enviar.`);
      await load();
      await download(doc.id, doc.version);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar o documento.");
    } finally { setBusy(false); }
  };

  const download = async (docId, version) => {
    try {
      const r = await api.get(`/patients/${patient.id}/documents/${docId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `condicoes-atendimento-v${version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Não consegui baixar o PDF."); }
  };

  const confirmSend = async () => {
    try {
      await api.post(`/patients/${patient.id}/documents/${lastDoc.id}/confirm-send`);
      toast.success("Envio confirmado e registrado.");
      load();
    } catch { toast.error("Erro ao confirmar envio."); }
  };

  return (
    <Dialog open={!!patient} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#5B6E5B]" /> Documento informativo
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-[#7A8A7A]">
            Informações e condições do atendimento — {patient.name}
          </DialogDescription>
        </DialogHeader>

        {!data || !cond ? <p className="font-body text-sm text-[#7A8A7A]">Carregando...</p> : (
          <div className="space-y-5" data-testid="doc-panel">
            {/* status */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={badge(st === "enviado" || st === "pronto_para_revisao")} data-testid="doc-status">{DOC_STATUS[st]}</span>
              {data.doc_status?.last_sent_at && (
                <span className="font-body text-xs text-[#7A8A7A]" data-testid="doc-last-sent">
                  Último envio confirmado em {fmtDate(data.doc_status.last_sent_at.slice(0, 10))}
                </span>
              )}
              <span className="font-body text-xs text-[#7A8A7A]">Texto-base v{data.template_version}{data.template_review_status === "em_revisao" ? " (em revisão)" : ""}</span>
            </div>
            {data.first_session && (
              <p className="font-body text-xs text-[#4A5D4E]" data-testid="doc-first-session">
                Primeira sessão registrada: {fmtDate(data.first_session.date)} às {data.first_session.time} — envie o documento antes dela.
              </p>
            )}
            {missing.length > 0 && (
              <div className="rounded-xl bg-[#C87A5E]/10 border border-[#C87A5E]/30 p-3" data-testid="doc-missing">
                <p className="font-body text-xs text-[#B2644D] flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Para emitir o documento, regularize: {missing.join(", ")}.
                </p>
              </div>
            )}

            {/* condições individuais */}
            <div className="rounded-xl border border-[#5B6E5B]/15 p-4 space-y-4">
              <p className="font-body text-xs uppercase tracking-wider text-[#5B6E5B]">Condições individuais do atendimento</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={label}>Valor da sessão (R$)</label>
                  <input type="number" step="0.01" className={input} value={cond.session_price} onChange={(e) => setC("session_price", e.target.value)} data-testid="cond-price" /></div>
                <div><label className={label}>Modalidade</label>
                  <select className={input} value={cond.modality} onChange={(e) => setC("modality", e.target.value)} data-testid="cond-modality">
                    <option value="">Selecionar</option><option value="online">Online</option><option value="presencial">Presencial</option>
                  </select></div>
                <div><label className={label}>Duração (min)</label>
                  <input type="number" className={input} value={cond.duration_min} onChange={(e) => setC("duration_min", e.target.value)} data-testid="cond-duration" /></div>
                <div><label className={label}>Frequência prevista</label>
                  <input className={input} placeholder="semanal, quinzenal..." value={cond.frequency} onChange={(e) => setC("frequency", e.target.value)} data-testid="cond-frequency" /></div>
                <div><label className={label}>Dia habitual</label>
                  <input className={input} placeholder="terça-feira" value={cond.usual_day} onChange={(e) => setC("usual_day", e.target.value)} data-testid="cond-day" /></div>
                <div><label className={label}>Horário habitual</label>
                  <input type="time" className={input} value={cond.usual_time} onChange={(e) => setC("usual_time", e.target.value)} data-testid="cond-time" /></div>
                <div><label className={label}>Forma de pagamento</label>
                  <input className={input} placeholder="Pix, cartão..." value={cond.payment_method} onChange={(e) => setC("payment_method", e.target.value)} data-testid="cond-paymethod" /></div>
                <div className="sm:col-span-2"><label className={label}>Prazo/vencimento</label>
                  <input className={input} placeholder="até o dia da sessão" value={cond.payment_due} onChange={(e) => setC("payment_due", e.target.value)} data-testid="cond-paydue" /></div>
              </div>
              <div><label className={label}>Política de cancelamentos</label>
                <textarea rows={2} className={`${input} resize-none`} value={cond.cancellation_policy} onChange={(e) => setC("cancellation_policy", e.target.value)} data-testid="cond-cancel-policy"
                  placeholder="Ex.: cancelamentos com menos de 24h serão cobrados integralmente." /></div>
              <div className="flex justify-end">
                <button onClick={saveCond} className={btnGhost} data-testid="cond-save">Salvar condições</button>
              </div>
            </div>

            {/* emissão + envio manual */}
            <div className="rounded-xl border border-[#5B6E5B]/15 p-4 space-y-3">
              <p className="font-body text-xs uppercase tracking-wider text-[#5B6E5B]">Emissão e envio (sempre manual)</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={generate} disabled={busy || missing.length > 0} className={btnPrimary} data-testid="doc-generate">
                  <FileText className="w-4 h-4" /> {busy ? "Gerando..." : lastDoc ? "Gerar nova versão (PDF)" : "Gerar PDF"}
                </button>
                {lastDoc && (
                  <button onClick={() => download(lastDoc.id, lastDoc.version)} className={btnGhost} data-testid="doc-download">
                    <Download className="w-4 h-4" /> Baixar PDF v{lastDoc.version}
                  </button>
                )}
              </div>
              {lastDoc && st !== "enviado" && (
                <div className="rounded-xl bg-[#5B6E5B]/6 p-3 space-y-2">
                  <p className="font-body text-xs text-[#4A5D4E]">
                    Como enviar: 1) baixe o PDF · 2) abra o WhatsApp com a mensagem pronta · 3) anexe o PDF na conversa · 4) confirme o envio aqui.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href={waLink(patient.phone, WA_MESSAGE)} target="_blank" rel="noreferrer" data-testid="doc-whatsapp"
                      className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm text-white hover:bg-[#1EBE5A] transition-colors">
                      <MessageCircle className="w-4 h-4" /> Abrir WhatsApp com mensagem
                    </a>
                    <button onClick={confirmSend} className={btnGhost} data-testid="doc-confirm-send">
                      <CheckCircle2 className="w-4 h-4" /> Confirmar que enviei
                    </button>
                  </div>
                  <p className="font-body text-[11px] text-[#7A8A7A]">O envio nunca é automático e só é registrado quando você confirma.</p>
                </div>
              )}
            </div>

            {/* histórico */}
            {data.documents?.length > 0 && (
              <div className="rounded-xl border border-[#5B6E5B]/15 p-4" data-testid="doc-history">
                <p className="font-body text-xs uppercase tracking-wider text-[#5B6E5B] mb-2">Versões emitidas</p>
                <div className="divide-y divide-[#5B6E5B]/10">
                  {data.documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-2" data-testid={`doc-version-${d.version}`}>
                      <p className="font-body text-sm text-[#2C332A]">
                        v{d.version} · {fmtDate((d.issued_at || "").slice(0, 10))} · texto-base v{d.template_version}
                        {d.sends?.length > 0 && <span className="text-[#4A5D4E]"> · enviado {fmtDate(d.sends[d.sends.length - 1].at.slice(0, 10))}</span>}
                      </p>
                      <button onClick={() => download(d.id, d.version)} className="text-[#5B6E5B] hover:text-[#2C332A]" data-testid={`doc-download-${d.version}`}>
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {canSend && false && <Send className="hidden" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
